import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import type { Business } from '../types/database';
import { setSelectedBusiness as saveSelectedBusiness, getSelectedBusiness as getSavedBusinessId } from '../lib/storage';

interface BusinessContextType {
  selectedBusiness: Business | null;
  businesses: Business[];
  setSelectedBusiness: (business: Business | null) => void;
  refreshBusinesses: () => Promise<void>;
  loading: boolean;
}

const BusinessContext = createContext<BusinessContextType | undefined>(undefined);

export function BusinessProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [selectedBusiness, setSelectedBusinessState] = useState<Business | null>(null);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const initialLoadDone = useRef(false);

  const fetchBusiness = useCallback(async (restoreFromStorage: boolean) => {
    const { data } = await supabase
      .from('businesses')
      .select('*')
      .order('created_at', { ascending: true });

    const fetched = data || [];
    setBusinesses(fetched);

    if (restoreFromStorage) {
      const savedId = await getSavedBusinessId();
      if (savedId) {
        const match = fetched.find(b => b.id === savedId);
        if (match) {
          setSelectedBusinessState(match);
          setLoading(false);
          return;
        }
      }
      setSelectedBusinessState(null);
    } else {
      setSelectedBusinessState(prev => {
        if (!prev) return null;
        const match = fetched.find(b => b.id === prev.id);
        return match || null;
      });
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setBusinesses([]);
      setSelectedBusinessState(null);
      setLoading(false);
      initialLoadDone.current = false;
      return;
    }
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      fetchBusiness(true);
    }
  }, [user, authLoading, fetchBusiness]);

  const refreshBusinesses = useCallback(async () => {
    await fetchBusiness(false);
  }, [fetchBusiness]);

  const setSelectedBusiness = async (business: Business | null) => {
    setSelectedBusinessState(business);
    if (business) {
      await saveSelectedBusiness(business.id);
    }
  };

  return (
    <BusinessContext.Provider
      value={{
        selectedBusiness,
        businesses,
        setSelectedBusiness,
        refreshBusinesses,
        loading,
      }}
    >
      {children}
    </BusinessContext.Provider>
  );
}

export function useBusiness() {
  const context = useContext(BusinessContext);
  if (context === undefined) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
}
